import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const Context = createContext(null);
export const useBillingLifecycle = () => useContext(Context);

export function BillingLifecycleProvider({ children }) {
  const { user } = useAuth();
  // In-memory cache belongs to this authenticated identity only.
  const identity = `${user?.id || ""}:${user?.org_id || ""}`;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState(false);
  const [valueSnapshot, setValueSnapshot] = useState(null);
  const [valueError, setValueError] = useState(false);
  const valueRequest = useRef(null);
  const valueCache = useRef(null);
  const inFlight = useRef(null);
  const data = snapshot?.identity === identity ? snapshot.data : null;
  const refresh = useCallback(async ({ reconcile = false } = {}) => {
    if (!user?.org_id) return null;
    if (inFlight.current?.identity === identity) return inFlight.current.promise;
    const request = { identity };
    request.promise = (async () => {
      try {
        if (reconcile) {
          // Explicit recovery only; never continuously poll Stripe.
          try { await api.post("/subscription/sync"); } catch { /* still read local state */ }
        }
        const response = await api.get("/billing/lifecycle");
        if (identityRef.current === identity) {
          setSnapshot({ identity, data: response.data });
          setError(false);
        }
        return response.data;
      } catch {
        if (identityRef.current === identity) setError(true);
        return null;
      } finally {
        if (inFlight.current === request) inFlight.current = null;
      }
    })();
    inFlight.current = request;
    return request.promise;
  }, [identity, user?.org_id]);

  const valueEligible = data?.plan === "trial" && data?.can_manage_billing;
  const trialValue = valueEligible && valueSnapshot?.identity === identity ? valueSnapshot.data : null;
  const refreshValue = useCallback(async () => {
    if (!valueEligible) return;
    if (valueCache.current?.identity === identity && Date.now() - valueCache.current.at < 300000) return;
    if (valueRequest.current?.identity === identity) return valueRequest.current.promise;
    const request = { identity };
    request.promise = api.get("/billing/trial-value").then(({ data: value }) => {
      if (identityRef.current !== identity) return;
      valueCache.current = { identity, at: Math.min(Date.now(), Date.parse(value.calculated_at) || Date.now()) };
      setValueSnapshot({ identity, data: value.available ? value : null });
      setValueError(false);
    }).catch(() => { if (identityRef.current === identity) setValueError(true); })
      .finally(() => { if (valueRequest.current === request) valueRequest.current = null; });
    valueRequest.current = request;
    return request.promise;
  }, [identity, valueEligible]);
  useEffect(() => {
    if (!valueEligible) return;
    refreshValue();
    const focus = () => refreshValue();
    const timer = setInterval(() => { if (document.visibilityState !== "hidden") refreshValue(); }, 300000);
    window.addEventListener("focus", focus);
    return () => { clearInterval(timer); window.removeEventListener("focus", focus); };
  }, [refreshValue, valueEligible]);

  useEffect(() => { setError(false); refresh(); }, [refresh]);
  useEffect(() => {
    const changed = () => refresh();
    const focus = () => {
      if (document.visibilityState === "hidden") return;
      if (data?.locked || data?.lifecycle_state === "payment_grace" || window.location.pathname.startsWith("/app/billing")) {
        refresh({ reconcile: !!data?.can_manage_billing });
      }
    };
    window.addEventListener("fieldlink:billing-changed", changed);
    window.addEventListener("focus", focus);
    return () => {
      window.removeEventListener("fieldlink:billing-changed", changed);
      window.removeEventListener("focus", focus);
    };
  }, [refresh, data]);
  useEffect(() => {
    // Re-evaluate local dates only while attention is needed. No Stripe calls.
    if (!data || data.lifecycle_state === "active") return;
    const timer = setInterval(() => {
      if (document.visibilityState !== "hidden") refresh();
    }, 60000);
    return () => clearInterval(timer);
  }, [data?.lifecycle_state, refresh]);
  return <Context.Provider value={{ data, error, refresh, trialValue, valueError, refreshValue }}>{children}</Context.Provider>;
}
